import { useChatContext } from '$/components/Chat/ChatContext';
import { propertyFilledText } from '$/components/Chat/utils/injectAttendeeFieldsInMessage';
import { useSocketContext } from '$/components/Socket/SocketContext';
import { WebinarPlayerUIState } from '$/components/WebinarPlayer/context';
import { AttendeeFragment, MessageSubType, MessageType } from '$/GraphQL/schema/types';
import { logger } from '$/utils/logger';
import React from 'react';
import { InteractionInfo } from '../InteractionInfo';
import { AttendeeChatMessages, ChatMessages } from '$/components/Chat/ChatMessages';
import { useGetConversation } from '$/components/Chat/hooks/useGetConversation';
import { useAttendeePropertiesContext } from '$/components/Property/PropertyProvider';

export const useSendPrivateMessage = (
  interaction: InteractionInfo,
  attendee: AttendeeFragment | undefined,
  {
    mode,
    ewebinar,
    playbackPositionRef,
    locale,
  }: Pick<WebinarPlayerUIState, 'mode' | 'ewebinar' | 'playbackPositionRef' | 'locale'>
): void => {
  const sentRef = React.useRef<boolean>(false);
  const { socket } = useSocketContext();
  const disabledChat = ChatMessages.isChatDisabled(mode, ewebinar, attendee?.sessionType);
  const { isCapturingUngatedAttendeeInfo, serverDateNow } = useChatContext();
  const { mentionField } = useAttendeePropertiesContext();
  const { conversation } = useGetConversation(attendee);

  // Run once
  const shouldFireRef = React.useRef(() => {
    return Math.floor(playbackPositionRef.current!) <= interaction.playbackPosition;
  });

  React.useEffect(() => {
    if (!shouldFireRef.current) {
      logger.debug('CHAT', 'Private Chat -> time is over', {
        pos: playbackPositionRef.current,
        interactionPos: interaction.playbackPosition,
      });
      return;
    }

    if (sentRef.current) {
      logger.debug('CHAT', 'Private Chat -> already fired');
      return;
    }

    if (!conversation?.id) {
      logger.debug('CHAT', 'Private Chat -> Waiting for conversation', conversation?.id);
      return;
    }

    if (!attendee || !interaction.details) return;

    logger.debug('CHAT', 'CAPTURING Attendee Info', isCapturingUngatedAttendeeInfo);
    if (isCapturingUngatedAttendeeInfo) {
      logger.debug('CHAT', 'CAPTURING UNGATED > IGNORE');
      return;
    }

    const description = interaction.details?.description;
    if (disabledChat || !description) return;

    const msgs = new AttendeeChatMessages(conversation.messages);
    const lastMessageFromModerator = msgs.getLastModeratorChatMessage();

    logger.debug('CHAT', 'Private Chat -> moderator chat?', lastMessageFromModerator);
    if (interaction.details?.skipWhenChatting && lastMessageFromModerator) {
      logger.debug('CHAT', 'Private Chat -> skipWhenChatting');
      return;
    }

    logger.debug(
      'CHAT',
      'Private Chat -> Firing',
      playbackPositionRef.current,
      interaction.appearAt(),
      interaction.id,
      interaction.details.description
    );

    const content = propertyFilledText(
      description,
      locale,
      ewebinar,
      mentionField.propertiesHash,
      attendee
    )!;

    const message = {
      attendeeId: attendee.id,
      uuid: interaction.shortId,
      timeSent: serverDateNow().toISOString(),
      content,
      interactionId: interaction.id,
      fromAttendee: false,
      type: MessageType.Automatic,
      subtype: MessageSubType.Private,
      timeInWebinarSecs: interaction.appearAt(),
    };

    socket?.send({ chat: message });
    sentRef.current = true;
  }, [
    ewebinar,
    attendee,
    interaction,
    socket,
    isCapturingUngatedAttendeeInfo,
    disabledChat,
    conversation,
    playbackPositionRef,
    shouldFireRef,
    mentionField.propertiesHash,
    locale,
    serverDateNow,
  ]);
};
